import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Dropdown, Toggle, Divider, Button} from '@/components/atoms';
import styles from '../RewardSetup.module.scss';
import SegmentsModal from './SegmentsModal';
import {Font} from '@pepsico-ds/ui';
import NonInteractiveDropdownTrigger from './NonInteractiveDropdownTrigger';
import useSegmentsGroups from '../../../../hooks/useSegmentsGroups';
import SegmentsGroupRow from './SegmentsGroupRow';
import SegmentChips from './SegmentChips';
import GroupsBreadcrumb from './SegmentGroupsBreadcrumb';
import {Constants, SEGMENT_CHIP_TYPE} from '@/utils/constants/commonConstants';

/**
 * SegmentsCard component displays the segments configuration section
 * Contains all customers toggle, segments selection, and exclude segments functionality
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing segments configuration
 * @param {Array} props.segments - Array of available segments
 * @param {boolean} props.segmentsLoading - Loading state for segments
 * @param {Function} props.handleAllCustomersChange - Handler for all customers toggle
 * @param {Function} props.handleSegmentExcludeChange - Handler for segment exclude toggle
 * @param {Function} props.handleSegmentsChange - Handler for segments selection
 * @returns {JSX.Element} The rendered SegmentsCard component
 */
const SegmentsCard = ({
  formData,
  segments,
  segmentsLoading,
  handleAllCustomersChange,
  handleSegmentExcludeChange,
  handleSegmentsChange,
  handleExcludedSegmentsChange,
  hideAddGroup = false,
  singleSelectMode = false,
  label = Constants.LabelSegments,
  title = Constants.TitleSegments,
  cardClassName = ''
}) => {
  const {
    groups,
    activeGroupIndex,
    excludeSelected,
    showSegmentModal,
    showExcludeModal,
    setShowExcludeModal,
    handleCancel,
    applyInclude,
    applyExclude,
    addGroup,
    deleteGroup,
    removeChip,
    removeExcludeChip,
    openGroupModal,
    openFirstSelection,
    groupPlaceholder
  } = useSegmentsGroups({
    formData,
    segments,
    handleSegmentsChange,
    handleExcludedSegmentsChange
  });

  const shouldShowDropdown = useMemo(() => {
    return (
      hideAddGroup ||
      groups.length === 0 ||
      (groups.length === 1 && groups.every((g) => g.items.length === 0))
    );
  }, [hideAddGroup, groups]);

  // Precompute include modal values for clarity
  const includeModalTitle = useMemo(() => {
    return activeGroupIndex === null && groups.length === 0
      ? Constants.IncludeModalTitleAll
      : `${Constants.IncludeModalTitleGroupPrefix} ${groups[activeGroupIndex]?.id || 1}`;
  }, [activeGroupIndex, groups]);

  const includeItems = useMemo(() => {
    if (activeGroupIndex !== null) {
      return groups[activeGroupIndex]?.items || [];
    }
    if (hideAddGroup) {
      return groups.flatMap((group) => group.items || []);
    } else {
      return groups[0]?.items || [];
    }
  }, [activeGroupIndex, groups, hideAddGroup]);
  const includeInitialSelectedIds = includeItems.map((s) => s.id);

  // Memoized placeholder for exclude dropdown to reduce JSX complexity
  const excludePlaceholder = useMemo(() => {
    if (excludeSelected.length > 1) return 'Multiple Selected';
    const first = excludeSelected[0];
    return (
      first?.label ||
      (segmentsLoading
        ? Constants.Loading
        : Constants.PlaceholderSelectSegments)
    );
  }, [excludeSelected, segmentsLoading]);

  return (
    <>
      <div
        id='segments-card'
        data-testid='segments-card'
        className={`flex flex-col gap-6 rounded-lg ${hideAddGroup ? 'py-6' : 'px-6 py-4'} ${styles.rewardSetup__card} ${cardClassName}`}>
        <h3
          id='segments-title'
          data-testid='segments-title'
          className='text-base font-bold text-white'>
          {title}
        </h3>
        <div className='flex flex-col gap-6'>
          <div
            id='segments-toggle-all'
            data-testid='segments-toggle-all'
            className={`flex items-center ${styles.rewardSetup__segments__toggleWrapper}`}>
            <span className={styles.rewardSetup__segments__toggleLabel}>
              {Constants.ToggleAllCustomers}
            </span>
            <Toggle
              value={formData.allCustomers}
              updateValue={handleAllCustomersChange}
              uiSchema={{
                isDark: true,
                size: 'small'
              }}
            />
          </div>
          {!formData.allCustomers && (
            <div className='flex flex-col gap-4'>
              {/* Always show dropdown for Activity (hideAddGroup=true) or when no segments */}
              {shouldShowDropdown && (
                <NonInteractiveDropdownTrigger
                  id='segments-include-dropdown-trigger'
                  testId='segments-include-dropdown-trigger'
                  ariaLabel='Open segment selection'
                  onOpen={openFirstSelection}>
                  <Dropdown
                    className=''
                    value={[]}
                    label={label}
                    updateValue={() => {}}
                    isDark={true}
                    childList={[]}
                    placeholder={Constants.PlaceholderSelectSegments}
                    selection='multiple'
                    isJsonWrap={false}
                    hideMenuListOnSelection={true}
                    preventTyping={true}
                  />
                </NonInteractiveDropdownTrigger>
              )}

              {groups.length > 0 &&
                groups.map((g, index) => (
                  <div
                    key={`group-${g.id}`}
                    id={`segments-group-${g.id}`}
                    data-testid={`segments-group-${g.id}`}
                    className={`flex flex-col gap-2`}>
                    {!hideAddGroup && (
                      <SegmentsGroupRow
                        group={g}
                        index={index}
                        groupPlaceholder={groupPlaceholder}
                        onOpen={openGroupModal}
                        onDelete={deleteGroup}
                      />
                    )}
                    {hideAddGroup && g.items.length === 0 && (
                      <SegmentsGroupRow
                        group={g}
                        index={index}
                        groupPlaceholder={groupPlaceholder}
                        onOpen={openGroupModal}
                        onDelete={deleteGroup}
                      />
                    )}
                    {g.items.length > 0 && (
                      <div
                        id={`segments-group-${g.id}-chips`}
                        data-testid={`segments-group-${g.id}-chips`}>
                        <SegmentChips
                          items={g.items}
                          onRemove={(segId) => removeChip(index, segId)}
                          type={SEGMENT_CHIP_TYPE.AND}
                        />
                      </div>
                    )}
                    {!hideAddGroup && <Divider className='mb-2 mt-3' />}
                  </div>
                ))}

              {!hideAddGroup && (
                <div
                  id='segments-groups-breadcrumb'
                  data-testid='segments-groups-breadcrumb'>
                  <GroupsBreadcrumb groups={groups} />
                </div>
              )}

              {/* +Add Group visible when at least one group has selections */}
              {!hideAddGroup && groups.some((g) => g.items.length > 0) && (
                <div id='segments-add-group' data-testid='segments-add-group'>
                  <Button
                    handleButton={addGroup}
                    uiSchema={{
                      title: 'Add Group',
                      size: 'small',
                      variant: 'text',
                      iconLeading: 'add_box',
                      isDark: true,
                      className:
                        'text-sm flex text-auxiliary items-center mt-0 mb-0'
                    }}
                  />
                </div>
              )}

              {/* Informational notes - hide for Activity */}
              {!hideAddGroup && groups.some((g) => g.items.length > 0) && (
                <div
                  id='segments-info-notes'
                  data-testid='segments-info-notes'
                  className='mt-1 flex flex-col gap-1 text-xs text-white/70'>
                  <Font size='xsmall'>
                    Within one group, users must be in all segments to see the
                    rewards.
                  </Font>
                  <Font size='xsmall'>
                    When there are multiple groups, users can be in either group
                    to see the rewards.
                  </Font>
                </div>
              )}
            </div>
          )}

          {!hideAddGroup && (
            <>
              <Divider className='mb-0 mt-1' />

              <div className='flex flex-col gap-6'>
                <div
                  id='segments-toggle-exclude'
                  data-testid='segments-toggle-exclude'
                  className={`flex items-center ${styles.rewardSetup__segments__toggleWrapper}`}>
                  <span className={styles.rewardSetup__segments__toggleLabel}>
                    {Constants.ToggleExclude}
                  </span>
                  <Toggle
                    value={formData.segmentExclude}
                    updateValue={handleSegmentExcludeChange}
                    uiSchema={{
                      isDark: true,
                      size: 'small'
                    }}
                  />
                </div>

                {formData.segmentExclude && (
                  <div className='flex flex-col gap-2'>
                    <NonInteractiveDropdownTrigger
                      id='segments-exclude-dropdown-trigger'
                      testId='segments-exclude-dropdown-trigger'
                      ariaLabel='Open exclude segment selection'
                      onOpen={() => setShowExcludeModal(true)}>
                      <Dropdown
                        className={`${styles.rewardSetup__segments_dropdown}`}
                        value={excludeSelected}
                        updateValue={() => {}}
                        label={Constants.ExcludeLabelSelect}
                        isDark={true}
                        childList={[]}
                        placeholder={excludePlaceholder}
                        selection='multiple'
                        isJsonWrap={false}
                      />
                    </NonInteractiveDropdownTrigger>
                    {excludeSelected.length > 0 && (
                      <div
                        id='segments-exclude-chips'
                        data-testid='segments-exclude-chips'>
                        <SegmentChips
                          items={excludeSelected}
                          onRemove={removeExcludeChip}
                          className='mt-1 flex flex-wrap items-center gap-2'
                          type={SEGMENT_CHIP_TYPE.OR}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showSegmentModal && (
        <SegmentsModal
          show={showSegmentModal}
          onClose={handleCancel}
          onApply={applyInclude}
          title={includeModalTitle}
          subtitle={Constants.ModalSubtitle}
          selectedSegments={includeItems}
          initialSelectedIds={includeInitialSelectedIds}
          singleSelectMode={singleSelectMode}
        />
      )}
      {!hideAddGroup && showExcludeModal && (
        <SegmentsModal
          show={showExcludeModal}
          onClose={handleCancel}
          onApply={applyExclude}
          title={Constants.ExcludeModalTitle}
          subtitle={Constants.ModalSubtitle}
          initialSelectedIds={excludeSelected.map((s) => s.id)}
        />
      )}
    </>
  );
};

SegmentsCard.propTypes = {
  formData: PropTypes.shape({
    allCustomers: PropTypes.bool,
    segmentExclude: PropTypes.bool,
    segments: PropTypes.array,
    excludedSegments: PropTypes.array
  }),
  segments: PropTypes.array,
  segmentsLoading: PropTypes.bool,
  handleAllCustomersChange: PropTypes.func,
  handleSegmentExcludeChange: PropTypes.func,
  handleSegmentsChange: PropTypes.func,
  handleExcludedSegmentsChange: PropTypes.func,
  hideAddGroup: PropTypes.bool,
  singleSelectMode: PropTypes.bool,
  title: PropTypes.string,
  cardClassName: PropTypes.string
};

export default React.memo(SegmentsCard);
